/**
 * Maps list UI column keys to metadata engine sort field keys.
 * URL / sort icons keep the UI key; API requests use the resolved key.
 */

const MODULE_SORT_FIELD_ALIASES = {
  ems_program_monthly_traffic: {
    training_program_name: "training_program_name",
    country_name: "country_name",
    city_name: "city_name",
  },
  ems_leads: {
    lead_stage_name: "lead_stage_id",
    training_program: "training_program_id",
    country: "country_id",
    location_region: "country_id",
    person_full_name: "person_full_name",
    lead_activities_count: "lead_activities_count",
    last_lead_activity_datetime: "last_lead_activity_datetime",
    assigned_counsellor_name: null,
    contact_status_dot: null,
    contact_timing_live: null,
  },
  ems_batch_classes: {
    batch: "organization_ems_batch_id",
    training_program: "training_program_id",
    trainer: "trainer_employee_id",
    batch_start_time: "start_time",
    batch_end_time: "end_time",
    location: "ems_training_space_id",
    last_record: null,
  },
  ems_students: {
    training_program: "training_program_id",
    person_full_name: "person_full_name",
  },
  ems_demo_sessions: {
    training_program: "training_program_id",
    trainer: "trainer_employee_id",
  },
  ems_demo_participants: {
    person_full_name: "person_full_name",
    training_program: "training_program_id",
  },
  ems_batch_students: {
    batch: "organization_ems_batch_id",
    student: "organization_ems_student_id",
    training_program: "training_program_id",
  },
  ems_admissions: {
    student: "organization_ems_student_id",
    training_program: "training_program_id",
  },
  ems_fee_installments: {
    student: "organization_ems_student_id",
    training_program: "training_program_id",
    admission: "organization_ems_admission_id",
    organization_ems_admission_id: "organization_ems_admission_id",
    inst_no: "installment_number",
    fee_inr: "fee_instalment_inr",
    fee_curr: "fee_instalment_student_student_currency",
    curr_code: "student_currency_code",
    due_date: "due_date",
    status: "status",
  },
  ems_student_fees: {
    student: "organization_ems_student_id",
    training_program: "training_program_id",
    payment_number: "payment_number",
  },
  ems_batches: {
    training_program: "training_program_id",
    trainer: "trainer_employee_id",
  },
  ems_batch_modules: {
    batch: "batch",
    module: "module",
    sequence_no: "sequence_no",
    status: "status",
    started_at: "started_at",
    completed_at: "completed_at",
  },
  ems_batch_class_records: {
    batch_class: "organization_ems_batch_class_id",
    trainer: "trainer_employee_id",
    class_date: "start_time",
    batch: "organization_ems_batch_id",
    training_program: "training_program_id",
    location: "ems_training_space_id",
    duration: "duration",
  },
  ems_class_attendance: {
    student: "organization_ems_student_id",
    batch_class: "organization_ems_batch_class_id",
  },
  ems_lead_activities: {
    person_full_name: "person_full_name",
    lead: "organization_ems_lead_id",
    lead_person_name: "organization_ems_lead_id",
    date_time: "activity_datetime",
    activity_id: "organization_ems_lead_activity_id",
    status: "status",
    call_status: "call_status",
    activity_type: "activity_type",
    lead_direction: "lead_direction",
    activity_by: "organization_person_id",
    lead_stage: "lead_stage",
  },
};

function resolveDisplaySortKey(columnKey) {
  if (
    typeof columnKey !== "string" ||
    !columnKey.startsWith("__") ||
    !columnKey.endsWith("_display")
  ) {
    return null;
  }
  // Datagrid display columns are resolved server-side via system_filter_metadata.ui_field_key.
  return null;
}

function resolveNameSortKey(columnKey) {
  if (typeof columnKey !== "string" || !columnKey.endsWith("_name")) {
    return null;
  }
  // Direct text columns (e.g. person_full_name) — not FK label → id mappings.
  if (columnKey.endsWith("_full_name")) {
    return null;
  }
  const base = columnKey.slice(0, -"_name".length);
  return base ? `${base}_id` : null;
}

/**
 * @param {string|null|undefined} moduleId
 * @param {string|null|undefined} columnKey
 * @returns {string|null} Resolved metadata field key, or null when not server-sortable.
 */
export function resolveMetadataSortField(moduleId, columnKey) {
  if (!columnKey) return null;

  const moduleAliases = MODULE_SORT_FIELD_ALIASES[moduleId];
  if (moduleAliases && Object.prototype.hasOwnProperty.call(moduleAliases, columnKey)) {
    return moduleAliases[columnKey];
  }

  const displayKey = resolveDisplaySortKey(columnKey);
  if (displayKey) return displayKey;

  const nameKey = resolveNameSortKey(columnKey);
  if (nameKey) return nameKey;

  return columnKey;
}

export function isMetadataSortableColumn(moduleId, columnKey) {
  return resolveMetadataSortField(moduleId, columnKey) !== null;
}

const NUMERIC_STRING_SORT_KEYS = new Set([
  "payment_number",
  "receipt_number",
  "student_fee_payment_number",
  "admission_number",
  "installment_number",
  "inst_no",
]);

export function shouldSortAsNumericString(columnKey) {
  return NUMERIC_STRING_SORT_KEYS.has(columnKey);
}

export function compareNumericStringValues(aVal, bVal, direction = "asc") {
  const aStr = String(aVal ?? "").trim().replace(/,/g, "");
  const bStr = String(bVal ?? "").trim().replace(/,/g, "");
  const aNum = Number(aStr);
  const bNum = Number(bStr);
  const aIsNum = aStr !== "" && Number.isFinite(aNum);
  const bIsNum = bStr !== "" && Number.isFinite(bNum);

  if (aIsNum && bIsNum) {
    return direction === "asc" ? aNum - bNum : bNum - aNum;
  }
  if (aIsNum && !bIsNum) return direction === "asc" ? -1 : 1;
  if (!aIsNum && bIsNum) return direction === "asc" ? 1 : -1;

  return direction === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
}
